/* eslint-disable react/prop-types */
import { useRecordContext, useRefresh } from 'react-admin';
import { Chip, Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { formatFileSize } from '../../util';

// Status configuration with colors and behavior
const STATUS_CONFIG = {
	pending_upload: { label: 'Pending Upload', color: 'default', clickable: true, showSpinner: true },
	pending: { label: 'Pending', color: 'default', clickable: true, showSpinner: true },
	extracted: { label: 'Extracted', color: 'success', clickable: true, showSpinner: true },
	content_empty: { label: 'No Content', color: 'warning', clickable: false },
	content_truncated: { label: 'Content Truncated', color: 'warning', clickable: true, showSpinner: true },
	indexing: { label: 'Indexing...', color: 'info', clickable: true, showSpinner: true },
	indexed: { label: 'Indexed', color: 'primary', clickable: false },
	reindexing: { label: 'Re-indexing...', color: 'info', clickable: true, showSpinner: true },
	extraction_failed: { label: 'Extraction Failed', color: 'error', clickable: true },
	indexing_failed: { label: 'Indexing Failed', color: 'error', clickable: true },
	unsupported_format: { label: 'Unsupported Format', color: 'warning', clickable: false },
	// Legacy status - kept for backwards compatibility
	uploaded: { label: 'Uploaded', color: 'default', clickable: false }
};

export const StatusChip = ({status}) => {
	const refresh = useRefresh();

	// If no status, show "(Unknown)" as plain text
	if (!status) {
		return <span>(Unknown)</span>;
	}

	const config = STATUS_CONFIG[status] || { label: status, color: 'default', clickable: false };

	const handleClick = () => {
		if (config.clickable) {
			refresh();
		}
	};

	const chipElement = (
		<Chip
			label={config.label}
			color={config.color}
			size="small"
			onClick={config.clickable ? handleClick : undefined}
			icon={config.showSpinner ? <CircularProgress size={16} color="inherit" /> : undefined}
			sx={{
				cursor: config.clickable ? 'pointer' : 'default',
				'&:hover': config.clickable ? { opacity: 0.8 } : {}
			}}
		/>
	);

	// Wrap clickable chips in tooltip
	if (config.clickable) {
		return (
			<Tooltip title="Click to refresh status">
				{chipElement}
			</Tooltip>
		);
	}

	return chipElement;
};


export const FileIcon = ({src}) => {
	const ext = src?.split('.').pop().toLowerCase();
	switch(ext) {
		case 'pdf': return <PictureAsPdfIcon color="error" />;
		case 'doc':
		case 'docx': return <DescriptionIcon color="primary" />;
		default: return <InsertDriveFileIcon />;
	}
};

export const DocumentFileField = () => {
	const record = useRecordContext();
	return (
        <Box sx={{
			width: .33,
            display: 'inline-block', 
            alignItems: 'center', 
            gap: 2,
            padding: 1,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            marginBottom: 1
        }}>
            <FileIcon {...record} />
            <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                    {record?.title}
                </Typography>
                {record?.byte_length && (
                    <Typography variant="caption" color="text.secondary">
                        {formatFileSize(record.byte_length)}
                    </Typography>
                )}
            </Box>
			<StatusChip {...record} />
        </Box>
	);
}